import { all, put, call, select, takeLatest, getContext } from 'redux-saga/effects';

import { Logger } from '../../utils/Logger';

import {
  myTeam,
  myNetwork,
  myCompany,
  REQUEST_VERIFY_TEAM_NAME,
  REQUEST_VERIFY_COMPANY_NAME,
  REQUEST_VERIFY_EMAIL_EXISTS,
  REQUEST_VERIFY_MULTIPLE_EMAIL_EXISTS,
  editTeam,
  viewTeam,
} from './types';

import {
  requestMyTeamSuccess,
  requestMyTeamFailure,
  requestMyNetworkSuccess,
  requestMyNetworkFailure,
  requestMyCompanySuccess,
  requestMyCompanyFailure,
  requestEditTeamSuccess,
  requestEditTeamFailure,
  requestViewTeamSuccess,
  requestViewTeamFailure,
} from './actions';

import { makeSelectUserCompanyId } from '../user/selectors';

import updateRoleSaga from './updateRole/sagas';
import myCompanyTeamsSaga from './myCompanyTeams/sagas';
import resendInviteSaga from './resendInvite/sagas';
import makeChampionSaga from './makeChampion/sagas';

const logger = new Logger('Saga:Teams');

export function* requestMyTeamsSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(authApiService.get, routerService.generate('api.teams.my_team'));

    yield put(requestMyTeamSuccess(response.data));
  } catch (error) {
    logger.info('Failed to get the current user teams', error);
    yield put(requestMyTeamFailure(error));
  }
}

export function* requestMyNetworkSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(authApiService.get, routerService.generate('api.teams.my_network'));

    yield put(requestMyNetworkSuccess(response.data));
  } catch (error) {
    logger.info('Failed to get the current user network', error);
    yield put(requestMyNetworkFailure(error));
  }
}

export function* requestMyCompanySaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(authApiService.get, routerService.generate('api.teams.my_company'));

    yield put(requestMyCompanySuccess(response.data));
  } catch (error) {
    logger.info('Failed to get the current user company', error);
    yield put(requestMyCompanyFailure(error));
  }
}

export function* requestVerifyCompanyNameSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.teams.verify_company_exists', {
        companyName: action.companyName,
      }),
    );

    action.promise && action.promise.resolve(response.data.exists);
  } catch (error) {
    logger.info('Failed to verify company name');
    action.promise && action.promise.reject(error);
  }
}

export function* requestVerifyEmailExistsSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.teams.verify_user_exists', { email: action.email }),
    );

    action.promise && action.promise.resolve(response.data.exists);
  } catch (error) {
    logger.info('Failed to verify if email exists', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestVerifyMultipleEmailExistsSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.teams.verify_users_exists'),
      { emails: action.emails },
    );

    const data = (response.data || []).reduce(
      (acc, item) => {
        const email = Object.keys(item)[0];
        const exists = item[email];

        !exists ? acc.valid.push(email) : acc.invalid.push(email);

        return acc;
      },
      { valid: [], invalid: [] },
    );

    action.promise && action.promise.resolve(data);
  } catch (error) {
    logger.info('Failed to verify if multiple email exists', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestVerifyTeamNameSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  const currentUserCompanyId = yield select(makeSelectUserCompanyId());

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.teams.verify_team_exists', {
        id: action.companyId || currentUserCompanyId,
        teamName: action.teamName,
      }),
    );

    action.promise && action.promise.resolve(response.data.exists);
  } catch (error) {
    logger.info('Failed to verify company name', error);
    action.promise && action.promise.reject(error);
  }
}

export function* requestEditTeamSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.teams.edit_team', { id: action.teamId }),
      action.payload,
    );

    yield put(requestEditTeamSuccess(response.data));
    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.info('Failed to edit team', error);
    yield put(requestEditTeamFailure(error));
    action.promise && action.promise.reject(error);
  }
}

export function* requestViewTeamSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.teams.view_team', { teamId: action.teamId }),
    );

    yield put(requestViewTeamSuccess(action.teamId, response.data));
  } catch (error) {
    logger.info('Failed to get team', error);
    yield put(requestViewTeamFailure(action.teamId, error));
  }
}

export default function* teamsSaga() {
  return yield all([
    takeLatest(myTeam.request, requestMyTeamsSaga),
    takeLatest(myNetwork.request, requestMyNetworkSaga),
    takeLatest(myCompany.request, requestMyCompanySaga),
    takeLatest(REQUEST_VERIFY_TEAM_NAME, requestVerifyTeamNameSaga),
    takeLatest(REQUEST_VERIFY_COMPANY_NAME, requestVerifyCompanyNameSaga),
    takeLatest(REQUEST_VERIFY_EMAIL_EXISTS, requestVerifyEmailExistsSaga),
    takeLatest(REQUEST_VERIFY_MULTIPLE_EMAIL_EXISTS, requestVerifyMultipleEmailExistsSaga),
    takeLatest(editTeam.request, requestEditTeamSaga),
    takeLatest(viewTeam.request, requestViewTeamSaga),
    updateRoleSaga(),
    myCompanyTeamsSaga(),
    resendInviteSaga(),
    makeChampionSaga(),
  ]);
}
